import { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { parseCsv, downloadCsv } from "@/lib/csv";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, FileText, CheckCircle2, AlertTriangle, Loader2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Fields the backend BulkImportRow accepts.
const FIELDS = [
  { key: "property_address", label: "Property address", required: true },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip_code", label: "ZIP" },
  { key: "bedrooms", label: "Bedrooms", numeric: true },
  { key: "bathrooms", label: "Bathrooms", numeric: true },
  { key: "sqft", label: "Sq ft", numeric: true },
  { key: "asking_price", label: "Asking price", numeric: true },
  { key: "estimated_arv", label: "Estimated ARV", numeric: true },
  { key: "estimated_repairs", label: "Estimated repairs", numeric: true },
  { key: "seller_name", label: "Seller name" },
  { key: "seller_phone", label: "Seller phone" },
  { key: "seller_email", label: "Seller email" },
  { key: "lead_source", label: "Lead source" },
  { key: "notes", label: "Notes" },
  { key: "stage", label: "Stage" },
];

const HEADER_ALIASES = {
  property_address: ["property_address", "address", "property address", "street", "street_address"],
  city: ["city"],
  state: ["state", "st"],
  zip_code: ["zip_code", "zip", "zipcode", "postal_code"],
  bedrooms: ["bedrooms", "beds", "bed"],
  bathrooms: ["bathrooms", "baths", "bath"],
  sqft: ["sqft", "sq_ft", "square_feet", "size"],
  asking_price: ["asking_price", "asking", "price", "list_price"],
  estimated_arv: ["estimated_arv", "arv", "after_repair_value"],
  estimated_repairs: ["estimated_repairs", "repairs", "rehab", "rehab_cost"],
  seller_name: ["seller_name", "seller", "owner", "owner_name"],
  seller_phone: ["seller_phone", "phone", "seller phone"],
  seller_email: ["seller_email", "email", "seller email"],
  lead_source: ["lead_source", "source"],
  notes: ["notes", "comments", "remarks"],
  stage: ["stage", "status"],
};

function autoMap(headers) {
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const headerToField = {};
  headers.forEach((h) => {
    const nh = norm(h);
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.some((a) => norm(a) === nh)) {
        headerToField[h] = field;
        return;
      }
    }
    headerToField[h] = "";  // skip
  });
  return headerToField;
}

export default function ImportLeadsModal({ onClose, onComplete }) {
  const { user } = useAuth();
  const isPaid = (user?.tier && user.tier !== "free") || user?.role === "admin";
  const isHustlerOrAbove = ["hustler", "team"].includes(user?.tier) || user?.role === "admin";
  const ROW_CAP_STARTER = 50;
  const [step, setStep] = useState("upload");  // upload | preview | importing | done
  const [csv, setCsv] = useState(null);        // { headers, rows }
  const [mapping, setMapping] = useState({});  // csvHeader → field
  const [skipDupes, setSkipDupes] = useState(true);
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const [bulkSkipTrace, setBulkSkipTrace] = useState(false);
  const [listName, setListName] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.rows.length === 0) {
      toast.error("CSV looks empty");
      return;
    }
    setCsv(parsed);
    setMapping(autoMap(parsed.headers));
    setStep("preview");
  };

  const previewRows = useMemo(() => {
    if (!csv) return [];
    return csv.rows.slice(0, 5).map((r) => {
      const mapped = {};
      for (const [csvH, field] of Object.entries(mapping)) {
        if (!field) continue;
        mapped[field] = r[csvH];
      }
      return mapped;
    });
  }, [csv, mapping]);

  const hasAddress = useMemo(() => Object.values(mapping).includes("property_address"), [mapping]);

  const onImport = async () => {
    if (!csv || !hasAddress) return;
    setStep("importing");
    // Transform rows according to the mapping; coerce numeric fields.
    const numericKeys = new Set(FIELDS.filter((f) => f.numeric).map((f) => f.key));
    const rows = csv.rows.map((r) => {
      const out = {};
      for (const [csvH, field] of Object.entries(mapping)) {
        if (!field) continue;
        let v = r[csvH];
        if (numericKeys.has(field)) {
          if (v === "" || v === null || v === undefined) { v = null; }
          else {
            const cleaned = String(v).replace(/[$,]/g, "").trim();
            const n = Number(cleaned);
            v = Number.isFinite(n) ? n : null;
          }
        }
        if (v !== null && v !== undefined) out[field] = v;
      }
      return out;
    });
    try {
      const r = await api.post("/leads/bulk-import", {
        rows,
        skip_duplicates: skipDupes,
        auto_analyze: autoAnalyze,
        bulk_skip_trace: bulkSkipTrace,
        list_name: listName.trim(),
      });
      setResult(r.data);
      setStep("done");
      if (onComplete) onComplete(r.data);
      if (r.data.created_count > 0) {
        toast.success(`Imported ${r.data.created_count} lead${r.data.created_count === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
      setStep("preview");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="leads-import-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Bulk import</div>
            <div className="font-display font-extrabold text-xl">Import Leads from CSV</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} data-testid="leads-import-close">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 flex-1">
          {step === "upload" && (
            <UploadStep fileRef={fileRef} onFile={onFile} />
          )}

          {step === "preview" && csv && (
            <PreviewStep
              csv={csv}
              mapping={mapping}
              setMapping={setMapping}
              hasAddress={hasAddress}
              previewRows={previewRows}
              skipDupes={skipDupes}
              setSkipDupes={setSkipDupes}
              autoAnalyze={autoAnalyze}
              setAutoAnalyze={setAutoAnalyze}
              bulkSkipTrace={bulkSkipTrace}
              setBulkSkipTrace={setBulkSkipTrace}
              isHustlerOrAbove={isHustlerOrAbove}
              isPaid={isPaid}
              rowCapStarter={ROW_CAP_STARTER}
              listName={listName}
              setListName={setListName}
            />
          )}

          {step === "importing" && (
            <div className="py-12 text-center text-[var(--ink-2)]" data-testid="leads-import-progress">
              <Loader2 size={28} className="animate-spin inline mb-3" />
              <div className="font-semibold">Importing {csv?.rows.length || 0} rows…</div>
              <div className="text-sm">This can take a few seconds. Don&apos;t close this window.</div>
            </div>
          )}

          {step === "done" && result && <DoneStep result={result} />}
        </div>

        <div className="flex justify-between gap-2 p-4 border-t bg-slate-50/50">
          {step === "preview" && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setCsv(null); setStep("upload"); }}
              data-testid="leads-import-back"
            >
              ← Pick another file
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {step === "done" ? "Close" : "Cancel"}
            </button>
            {step === "preview" && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!hasAddress}
                onClick={onImport}
                data-testid="leads-import-submit"
              >
                Import {csv?.rows.length || 0} rows
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadStep({ fileRef, onFile }) {
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const onDownloadTemplate = (e) => {
    e.stopPropagation();
    const columns = FIELDS.map((f) => ({ label: f.key, accessor: (r) => r[f.key] ?? "" }));
    const samples = [
      {
        property_address: "123 Main St", city: "Austin", state: "TX", zip_code: "78704",
        bedrooms: 3, bathrooms: 2, sqft: 1450,
        asking_price: 245000, estimated_arv: 385000, estimated_repairs: 38000,
        seller_name: "Jane Doe", seller_phone: "512-555-0101", seller_email: "jane@example.com",
        lead_source: "Direct Mail", notes: "Probate; motivated.", stage: "New Lead",
      },
      {
        property_address: "456 Oak Ave", city: "Phoenix", state: "AZ", zip_code: "85016",
        bedrooms: 4, bathrooms: 2, sqft: 1800,
        asking_price: 310000, estimated_arv: 465000, estimated_repairs: 52000,
        seller_name: "John Smith", seller_phone: "602-555-0144", seller_email: "",
        lead_source: "PPC", notes: "Pre-foreclosure; needs roof.", stage: "Contacted",
      },
    ];
    downloadCsv("ispeedtoclose-leads-template.csv", columns, samples);
    toast.success("Template downloaded — edit it in Excel/Sheets, then drop it back here.");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="leads-import-upload">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-[var(--primary)] transition-colors"
        data-testid="leads-import-dropzone"
      >
        <Upload size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
        <div className="font-semibold text-[var(--ink)]">Drop your CSV here or click to choose</div>
        <div className="text-xs text-[var(--ink-2)] mt-2">Max 5 MB · 1000 rows per import</div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          data-testid="leads-import-file-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>
      <div className="card p-4 bg-slate-50 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-2)] mb-2">First time?</div>
          <div className="text-sm text-[var(--ink-2)]">
            Grab our template — pre-formatted with the exact 16 column headers and 2 example rows. Edit in Excel/Google Sheets, then drop it back here.
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ghost flex-shrink-0"
          onClick={onDownloadTemplate}
          data-testid="leads-import-template-btn"
        >
          <Download size={14} /> Download template
        </button>
      </div>
    </div>
  );
}

function PreviewStep({ csv, mapping, setMapping, hasAddress, previewRows, skipDupes, setSkipDupes, autoAnalyze, setAutoAnalyze, bulkSkipTrace, setBulkSkipTrace, isHustlerOrAbove, isPaid, rowCapStarter, listName, setListName }) {
  return (
    <div className="flex flex-col gap-4" data-testid="leads-import-preview">
      <div className="card p-3 bg-emerald-50/40 border-emerald-200">
        <div className="text-sm font-semibold flex items-center gap-2">
          <FileText size={14} className="text-emerald-700" />
          {csv.rows.length} row{csv.rows.length === 1 ? "" : "s"} detected · {csv.headers.length} columns
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-2)] mb-2">Column mapping</div>
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Your CSV header</th>
                <th className="px-3 py-2 font-semibold">Maps to iSpeedToClose field</th>
              </tr>
            </thead>
            <tbody>
              {csv.headers.map((h) => (
                <tr key={h} className="border-t" data-testid={`map-row-${h}`}>
                  <td className="px-3 py-2 font-mono text-xs">{h || "(empty)"}</td>
                  <td className="px-3 py-2">
                    <select
                      className="input max-w-xs"
                      value={mapping[h] || ""}
                      onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                      data-testid={`map-select-${h}`}
                    >
                      <option value="">— skip this column —</option>
                      {FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}{f.required ? " *" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!hasAddress && (
          <div className="mt-2 text-sm text-rose-700 flex items-center gap-1" data-testid="leads-import-warn-address">
            <AlertTriangle size={12} /> Map at least one column to <strong>Property address</strong> to continue.
          </div>
        )}
      </div>

      {previewRows.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-2)] mb-2">Preview (first 5 rows after mapping)</div>
          <div className="card overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left">
                <tr>
                  {Object.keys(previewRows[0] || {}).map((k) => (
                    <th key={k} className="px-2 py-1.5 font-semibold whitespace-nowrap">{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, i) => (
                  <tr key={i} className="border-t">
                    {Object.keys(previewRows[0] || {}).map((k) => (
                      <td key={k} className="px-2 py-1.5 whitespace-nowrap truncate max-w-[180px]" title={r[k] || ""}>
                        {r[k] || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-3 flex flex-col gap-2 bg-slate-50/40">
        {!isPaid && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 mb-1" data-testid="import-upgrade-nudge">
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-indigo-600 mt-0.5 shrink-0" />
              <div className="text-xs leading-snug text-indigo-900">
                <strong>Pro unlocks List Stacking.</strong> Tag every import with a list name so addresses appearing on
                {" "}<strong>2, 3, or 4+ lists</strong> auto-bubble to the top — the wholesaler edge that converts at 3-5× baseline.
                {" "}Free tier is capped at <strong>{rowCapStarter} rows per import</strong> and stacking is disabled.
                <a href="/v3/business/billing" className="underline font-bold ml-1">Upgrade →</a>
              </div>
            </div>
          </div>
        )}
        {!isHustlerOrAbove && isPaid && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="import-hustler-nudge">
            <div className="text-[11px] leading-snug text-amber-900">
              ⚡ <strong>Hustler unlocks Auto-Distress Enrichment</strong> — your imports get pre-foreclosure, tax-delinquent, vacant, and absentee flags auto-applied from your CSV columns. <a href="/v3/business/billing" className="underline font-bold">See Hustler →</a>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)] shrink-0">
            List name
          </label>
          <input
            className="input flex-1 text-sm"
            placeholder={isPaid ? "e.g. Phoenix Probate Q1 (optional — enables list stacking)" : "Upgrade to Pro to enable list stacking"}
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            data-testid="leads-import-list-name"
            maxLength={120}
            disabled={!isPaid}
          />
        </div>
        <p className="text-xs text-[var(--ink-3)] leading-snug">
          Tip: tag every import. If an address shows up on 3+ lists (probate + tax-delinquent + absentee, say) it&apos;s almost always your hottest opportunity.
          {" "}
          <a
            href="/resources#stack-recipes"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 font-semibold hover:underline"
            data-testid="import-stack-recipe-link"
          >
            🍳 Browse Stack Recipes →
          </a>
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipDupes}
            onChange={(e) => setSkipDupes(e.target.checked)}
            data-testid="leads-import-skip-dupes"
          />
          <span>Skip duplicate addresses ({listName && isPaid ? "they'll stack onto the existing lead's list-count" : "duplicates are dropped"})</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoAnalyze}
            onChange={(e) => setAutoAnalyze(e.target.checked)}
            data-testid="leads-import-auto-analyze"
          />
          <span>Run AI Deal Analysis on each imported lead (may consume your monthly AI quota)</span>
        </label>
        <label
          className={"flex items-center gap-2 text-sm " + (isHustlerOrAbove ? "" : "opacity-60 cursor-not-allowed")}
          title={isHustlerOrAbove ? "" : "Upgrade to Hustler to skip-trace at import time"}
        >
          <input
            type="checkbox"
            checked={bulkSkipTrace}
            disabled={!isHustlerOrAbove}
            onChange={(e) => setBulkSkipTrace(e.target.checked)}
            data-testid="leads-import-bulk-skip-trace"
          />
          <span className="inline-flex items-center gap-1">
            <Sparkles size={12} className="text-amber-500" />
            Bulk skip-trace newly imported leads
            {!isHustlerOrAbove && (
              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                Hustler+
              </span>
            )}
            <span className="text-[var(--ink-3)]">
              {isHustlerOrAbove
                ? "(uses your monthly skip-trace credits — partial pass if quota runs out)"
                : "— unlock with Hustler tier"}
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

function DoneStep({ result }) {
  const sts = result.skip_trace_summary;
  return (
    <div className="flex flex-col gap-3" data-testid="leads-import-done">
      <div className="card p-5 text-center bg-emerald-50/40 border-emerald-200">
        <CheckCircle2 size={32} className="mx-auto text-emerald-600 mb-2" />
        <div className="font-display font-extrabold text-2xl">{result.created_count} leads imported</div>
        <div className="text-sm text-[var(--ink-2)] mt-1">
          out of {result.total_submitted} submitted
        </div>
        {result.remaining_quota !== null && (
          <div className="text-xs text-[var(--ink-3)] mt-2">
            {result.remaining_quota} slot{result.remaining_quota === 1 ? "" : "s"} remaining on your plan
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Tile label="Created" value={result.created_count} accent="emerald" testid="import-tile-created" />
        <Tile label="Skipped" value={result.skipped_count} accent="amber" testid="import-tile-skipped" />
        <Tile label="Failed" value={result.failed_count} accent="rose" testid="import-tile-failed" />
      </div>

      {sts && (
        <div
          className={"card p-4 " + (sts.tier_locked ? "bg-amber-50/40 border-amber-200" : "bg-violet-50/40 border-violet-200")}
          data-testid="import-skip-trace-summary"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles size={14} className={sts.tier_locked ? "text-amber-600" : "text-violet-700"} />
            <div className="font-bold text-sm">
              {sts.tier_locked ? "Bulk skip-trace skipped" : "Skip-trace pass complete"}
            </div>
          </div>
          {sts.tier_locked ? (
            <div className="text-xs text-[var(--ink-2)]">{sts.message}</div>
          ) : (
            <div className="text-xs text-[var(--ink-2)]">
              <strong className="text-violet-800">{sts.traced} traced</strong>
              {sts.deferred > 0 && <> · <strong>{sts.deferred} deferred</strong> (monthly quota exhausted — will pick up next cycle)</>}
              {sts.failed > 0 && <> · <strong className="text-rose-700">{sts.failed} failed</strong></>}
            </div>
          )}
        </div>
      )}

      {(result.skipped?.length || 0) > 0 && (
        <Details title={`Skipped (${result.skipped_count})`} rows={result.skipped} formatter={(r) => `${r.address || "—"} — ${r.reason}`} testid="import-skipped-list" />
      )}
      {(result.failed?.length || 0) > 0 && (
        <Details title={`Failed (${result.failed_count})`} rows={result.failed} formatter={(r) => `Row #${r.row_index} — ${r.reason}: ${r.detail || ""}`} testid="import-failed-list" />
      )}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  }[accent];
  return (
    <div className={"card p-3 text-center " + palette} data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
}

function Details({ title, rows, formatter, testid }) {
  return (
    <details className="card p-3" data-testid={testid}>
      <summary className="cursor-pointer text-sm font-semibold">{title}</summary>
      <div className="mt-2 flex flex-col gap-1 text-xs text-[var(--ink-2)] max-h-40 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="border-b pb-1 last:border-b-0">{formatter(r)}</div>
        ))}
        {rows.length === 50 && (
          <div className="italic text-[var(--ink-3)]">… (showing first 50)</div>
        )}
      </div>
    </details>
  );
}
